import { useMemo, useState, useEffect, type ChangeEvent } from 'react';
import { TextField, type TextFieldProps } from './TextField';

export interface CurrencyFieldProps
  extends Omit<TextFieldProps, 'value' | 'defaultValue' | 'onChange' | 'numeric' | 'type'> {
  /** Raw numeric value (in dollars). */
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
}

const formatter = new Intl.NumberFormat('en-US');

function format(n: number | undefined): string {
  if (n === undefined || Number.isNaN(n)) return '';
  return `$${formatter.format(Math.round(n))}`;
}

function parse(s: string): number {
  const digits = s.replace(/[^0-9]/g, '');
  if (!digits) return 0;
  return Number(digits);
}

export function CurrencyField({
  value,
  defaultValue,
  onValueChange,
  ...rest
}: CurrencyFieldProps) {
  const controlled = value !== undefined;
  const [internal, setInternal] = useState<string>(() => format(value ?? defaultValue));

  // Keep internal text in sync with controlled value.
  useEffect(() => {
    if (controlled) setInternal(format(value));
  }, [controlled, value]);

  const display = useMemo(() => (controlled ? format(value) : internal), [controlled, value, internal]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const parsed = parse(e.target.value);
    const formatted = format(parsed);
    if (!controlled) setInternal(formatted);
    onValueChange?.(parsed);
  };

  return (
    <TextField
      {...rest}
      type="text"
      inputMode="numeric"
      numeric
      value={display}
      onChange={handleChange}
    />
  );
}
